import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Star, X } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { API_BASE_URL } from '@/config/api';
import { useFavoritesStore } from '../../stores/favoritesStore';

interface AddFavoriteButtonProps {
  latitude: number;
  longitude: number;
  locationName: string;
  onSuccess?: () => void;
}

export function AddFavoriteButton({ 
  latitude, 
  longitude, 
  locationName,
  onSuccess, 
}: AddFavoriteButtonProps) {
  const { t } = useTranslation();
  const { token, isAuthenticated } = useAuthStore();
  const addLocalFavorite = useFavoritesStore((state) => state.addFavorite);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleAddFavorite = async () => {
    if (!isAuthenticated || !token) {
      addLocalFavorite({ name: locationName, latitude, longitude });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
      onSuccess?.();
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/favorites`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: locationName,
          latitude,
          longitude,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        let errorMessage = data.error || 'Fehler beim Hinzufügen des Favoriten';
        let is409 = false;
        
        // Handle specific error cases
        if (response.status === 409) {
          errorMessage = 'Dieser Ort ist bereits in Ihren Favoriten';
          is409 = true;
        }
        
        // For 409, treat as success (already favorited)
        if (is409) {
          setSuccess(true);
          setTimeout(() => setSuccess(false), 2000);
          onSuccess?.();
        } else {
          throw new Error(errorMessage);
        }
      } else {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
        onSuccess?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic'));
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      <motion.button
        onClick={handleAddFavorite}
        disabled={isLoading || success}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors
          ${success 
            ? 'bg-green-500 text-white' 
            : 'bg-yellow-500 hover:bg-yellow-600 text-white'
          }
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        {success ? (
          <>
            <X className="w-5 h-5" />
            Gespeichert!
          </>
        ) : (
          <>
            <Star className="w-5 h-5" />
            {isLoading ? t('favorites.saving') : t('favorites.add')}
          </>
        )}
      </motion.button>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="absolute top-full left-0 right-0 mt-2 p-2 bg-danger/10 border border-danger text-danger rounded text-sm z-50"
        >
          {error}
        </motion.div>
      )}
    </div>
  );
}
